/* =========================
   PEGUU — CART COUPON
========================= */

(() => {
  "use strict";

  const waitForPeguuCart = (timeoutMs = 5000) => {
    if (typeof window.PeguuCart?.getCurrentCart === "function") {
      return Promise.resolve(window.PeguuCart);
    }

    return new Promise((resolve, reject) => {
      let timeoutId = null;

      const cleanup = () => {
        document.removeEventListener("peguu:cart:ready", handleReady);

        if (timeoutId) {
          window.clearTimeout(timeoutId);
        }
      };

      const handleReady = () => {
        if (typeof window.PeguuCart?.getCurrentCart !== "function") {
          return;
        }

        cleanup();
        resolve(window.PeguuCart);
      };

      document.addEventListener("peguu:cart:ready", handleReady);

      timeoutId = window.setTimeout(() => {
        cleanup();
        reject(new Error("El carrito no está disponible en este momento."));
      }, timeoutMs);

      handleReady();
    });
  };

  const normalizeText = (value) => String(value ?? "").trim();

  const escapeHtml = (value) =>
    normalizeText(value)
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/"/g, "&quot;")
      .replace(/'/g, "&#039;");

  const formatPercent = (value) => {
    const amount = Number(value);

    if (!Number.isFinite(amount) || amount <= 0) {
      return "";
    }

    return `−${new Intl.NumberFormat("es-MX", {
      maximumFractionDigits: 0,
    }).format(amount)}%`;
  };

  const formatMoney = (value) => {
    const amount = Number(value);

    if (!Number.isFinite(amount)) {
      return "";
    }

    return new Intl.NumberFormat("es-MX", {
      style: "currency",
      currency: "MXN",
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(amount);
  };

  const formatCouponDate = (value) => {
    const date = value ? new Date(value) : null;

    if (!date || Number.isNaN(date.getTime())) {
      return "";
    }

    return new Intl.DateTimeFormat("es-MX", {
      day: "numeric",
      month: "short",
      year: "numeric",
    }).format(date);
  };

  const rateLimitMessage =
    "Demasiados intentos con cupones. Espera un momento y vuelve a intentar.";

  const AUTOMATIC_COUPON_CODE = "CLIENTEPEGUU";

  const isPromotionalCoupon = (coupon) => {
    const code = normalizeText(coupon?.code).toUpperCase();
    return Boolean(code) && code !== AUTOMATIC_COUPON_CODE;
  };

  class PeguuCartCoupon {
    constructor(root) {
      this.root = root;
      this.applied = root.querySelector("[data-cart-coupon-applied]");
      this.appliedCode = root.querySelector("[data-cart-coupon-applied-code]");
      this.appliedPercent = root.querySelector(
        "[data-cart-coupon-applied-percent]"
      );
      this.removeButton = root.querySelector("[data-cart-coupon-remove]");
      this.form = root.querySelector("[data-cart-coupon-form]");
      this.input = root.querySelector("[data-cart-coupon-input]");
      this.applyButton = root.querySelector("[data-cart-coupon-apply]");
      this.error = root.querySelector("[data-cart-coupon-error]");
      this.available = root.querySelector("[data-cart-coupon-available]");
      this.options = root.querySelector("[data-cart-coupon-options]");
      this.showAvailableCoupons =
        root.dataset.showAvailableCoupons === "true";
      this.trashIconUrl = normalizeText(root.dataset.trashIconUrl);
      this.isBusy = false;
      this.currentCart = null;
      this.appliedSignature = "";

      this.bindEvents();
    }

    bindEvents() {
      this.form?.addEventListener("submit", (event) => {
        event.preventDefault();
        this.applyCode(this.input?.value);
      });

      this.applied?.addEventListener("click", (event) => {
        const button = event.target.closest("[data-cart-coupon-remove]");

        if (!button) {
          return;
        }

        event.preventDefault();
        this.removeCode(button.dataset.couponCode);
      });

      this.options?.addEventListener("click", (event) => {
        const button = event.target.closest("[data-cart-coupon-option-apply]");

        if (!button) {
          return;
        }

        event.preventDefault();
        this.applyCode(button.dataset.couponCode);
      });
    }

    setBusy(isBusy) {
      this.isBusy = isBusy;
      this.root.classList.toggle("is-busy", isBusy);
      this.root.setAttribute("aria-busy", String(isBusy));

      if (this.input) {
        this.input.disabled = isBusy;
      }

      if (this.applyButton) {
        this.applyButton.disabled = isBusy;
      }

      this.root
        .querySelectorAll("[data-cart-coupon-remove]")
        .forEach((button) => {
          button.disabled = isBusy;
        });
    }

    showError(message) {
      if (!this.error) {
        return;
      }

      this.error.textContent = message;
      this.error.hidden = !message;
    }

    renderAvailable(coupons, appliedCodes) {
      if (!this.showAvailableCoupons) {
        if (this.options) {
          this.options.innerHTML = "";
        }

        if (this.available) {
          this.available.hidden = true;
        }

        return;
      }

      const list = Array.isArray(coupons) ? coupons : [];
      const applied = new Set(
        (Array.isArray(appliedCodes) ? appliedCodes : [appliedCodes])
          .map((code) => normalizeText(code).toUpperCase())
          .filter(Boolean)
      );
      const visible = list.filter((coupon) => {
        const code = normalizeText(coupon?.code).toUpperCase();
        return (
          Boolean(code) &&
          code !== AUTOMATIC_COUPON_CODE &&
          !applied.has(code)
        );
      });

      if (!this.available || !this.options) {
        return;
      }

      if (!visible.length) {
        this.options.innerHTML = "";
        this.available.hidden = true;
        return;
      }

      this.options.innerHTML = visible
        .map((coupon) => {
          const code = escapeHtml(coupon.code);
          const percent = formatPercent(coupon.discountPercent);
          const savings = formatMoney(coupon.discountAmount);
          const endsAt = formatCouponDate(coupon.endsAt);
          const badges = [
            percent
              ? `<span class="peguu-cart-coupon__badge">${escapeHtml(percent)}</span>`
              : "",
            coupon.firstOrderOnly
              ? `<span class="peguu-cart-coupon__badge peguu-cart-coupon__badge--first">Primer pedido</span>`
              : "",
          ].join("");
          const metaParts = [
            savings ? `Ahorras ${savings} en este carrito` : "",
            endsAt ? `Válido hasta ${endsAt}` : "",
          ].filter(Boolean);

          return `
            <li class="peguu-cart-coupon__option">
              <div class="peguu-cart-coupon__option-main">
                <div class="peguu-cart-coupon__option-name">
                  <span>${escapeHtml(coupon.name || coupon.code)}</span>
                  ${badges}
                </div>
                ${
                  metaParts.length
                    ? `<p class="peguu-cart-coupon__option-meta">${escapeHtml(
                        metaParts.join(" · ")
                      )}</p>`
                    : ""
                }
              </div>
              <button
                class="peguu-cart-coupon__option-apply"
                type="button"
                data-cart-coupon-option-apply
                data-coupon-code="${code}"
              >
                Usar
              </button>
            </li>
          `;
        })
        .join("");

      this.available.hidden = false;
    }

    render(cart, coupons = []) {
      const items = Array.isArray(cart?.items) ? cart.items : [];
      const hasItems = items.length > 0;
      const coupon = cart?.coupon || null;
      const appliedCoupons = Array.isArray(cart?.coupons)
        ? cart.coupons.filter((item) => normalizeText(item?.code))
        : coupon
          ? [coupon]
          : [];
      const promotionalCoupon =
        appliedCoupons.find(isPromotionalCoupon) || null;
      const visibleAppliedCoupons = promotionalCoupon
        ? [promotionalCoupon]
        : [];
      const couponError = this.isBusy
        ? normalizeText(cart?.couponError)
        : "";

      this.currentCart = cart;
      this.root.hidden = !hasItems;

      if (!hasItems) {
        this.showError("");
        this.renderAvailable([]);
        return;
      }

      if (this.applied) {
        this.applied.hidden = !visibleAppliedCoupons.length;
        const appliedSignature = JSON.stringify(
          visibleAppliedCoupons.map((item) => [
            normalizeText(item.code).toUpperCase(),
            Number(item.discountPercent) || 0,
            isPromotionalCoupon(item),
          ])
        );

        if (appliedSignature !== this.appliedSignature) {
          this.applied.innerHTML = visibleAppliedCoupons
            .map((item) => {
              const code = normalizeText(item.code).toUpperCase();
              const percent = formatPercent(item.discountPercent);
              const removable =
                isPromotionalCoupon(item);

              return `
                <span class="peguu-cart-coupon__applied-item">
                  <span class="peguu-cart-coupon__chip">
                    <span data-cart-coupon-applied-code>${escapeHtml(code)}</span>
                    ${percent ? `<span class="peguu-cart-coupon__chip-percent" data-cart-coupon-applied-percent>${escapeHtml(percent)}</span>` : ""}
                  </span>
                  ${
                    removable
                      ? `<button
                          class="peguu-cart-coupon__remove"
                          type="button"
                          data-cart-coupon-remove
                          data-coupon-code="${escapeHtml(code)}"
                          aria-label="Quitar cupón ${escapeHtml(code)}"
                        >
                          <img
                            class="peguu-cart-coupon__remove-icon"
                            src="${escapeHtml(this.trashIconUrl)}"
                            alt=""
                            aria-hidden="true"
                            width="23"
                            height="23"
                          >
                        </button>`
                      : ""
                  }
                </span>
              `;
            })
            .join("");
          this.appliedSignature = appliedSignature;
        }
      }

      if (this.form) {
        this.form.hidden = Boolean(promotionalCoupon);
      }

      if (this.input && document.activeElement !== this.input) {
        if (!couponError) {
          this.input.value = "";
        }
      }

      this.showError(couponError);
      this.renderAvailable(
        promotionalCoupon ? [] : coupons,
        appliedCoupons.map((item) => item?.code)
      );
    }

    async applyCode(rawCode) {
      const code = normalizeText(rawCode).toUpperCase();
      const appliedCodes = (Array.isArray(this.currentCart?.coupons)
        ? this.currentCart.coupons
        : this.currentCart?.coupon
          ? [this.currentCart.coupon]
          : [])
        .filter(isPromotionalCoupon)
        .map((coupon) => normalizeText(coupon?.code).toUpperCase())
        .filter(Boolean);

      if (this.isBusy) {
        return;
      }

      if (!code) {
        this.showError("");
        return;
      }

      if (appliedCodes.includes(code)) {
        this.showError("Este cupón ya está aplicado.");
        this.input?.focus();
        return;
      }

      if (appliedCodes.length) {
        this.showError(
          "Solo puedes usar un cupón por pedido. Quita el actual para usar otro."
        );
        return;
      }

      this.setBusy(true);
      this.showError("");

      try {
        const cartApi = await waitForPeguuCart();
        const cart = await cartApi.applyCoupon(code);
        this.render(
          cart,
          this.showAvailableCoupons
            ? await cartApi.getAvailableCoupons(cart)
            : []
        );
      } catch (error) {
        const message =
          Number(error?.status) === 429
            ? rateLimitMessage
            : error?.message || "No fue posible aplicar el cupón.";

        this.showError(message);

        if (this.input) {
          this.input.value = code;
        }
      } finally {
        this.setBusy(false);
      }
    }

    async removeCode(rawCode) {
      const code = normalizeText(rawCode).toUpperCase();

      if (this.isBusy) {
        return;
      }

      if (!code) {
        this.showError("No se pudo identificar el cupón que deseas quitar.");
        return;
      }

      this.setBusy(true);
      this.showError("");

      try {
        const cartApi = await waitForPeguuCart();
        const cart = await cartApi.removeCoupon(code);
        this.render(
          cart,
          this.showAvailableCoupons
            ? await cartApi.getAvailableCoupons(cart)
            : []
        );
      } catch (error) {
        const message =
          Number(error?.status) === 429
            ? rateLimitMessage
            : error?.message || "No fue posible quitar el cupón.";

        this.showError(message);
      } finally {
        this.setBusy(false);
      }
    }

    async refresh(cart) {
      try {
        const cartApi = await waitForPeguuCart();
        const currentCart = cart || (await cartApi.getCurrentCart());
        const coupons = this.showAvailableCoupons
          ? await cartApi.getAvailableCoupons(currentCart)
          : [];
        this.render(currentCart, coupons);
      } catch (error) {
        this.render(cart || this.currentCart || { items: [] }, []);
      }
    }
  }

  const instances = new Set();

  const initialize = (scope = document) => {
    const roots = scope.matches?.("[data-peguu-cart-coupon]")
      ? [scope]
      : Array.from(scope.querySelectorAll("[data-peguu-cart-coupon]"));

    roots.forEach((root) => {
      if (root.dataset.peguuInitialized === "true") {
        return;
      }

      root.dataset.peguuInitialized = "true";
      const instance = new PeguuCartCoupon(root);
      root.peguuCartCoupon = instance;
      instances.add(instance);
      instance.refresh().catch(() => {});
    });
  };

  const refreshAll = (cart) => {
    instances.forEach((instance) => {
      if (instance.root.isConnected) {
        instance.refresh(cart).catch(() => {});
      } else {
        instances.delete(instance);
      }
    });
  };

  document.addEventListener("peguu:cart:updated", (event) => {
    refreshAll(event.detail?.cart);
  });
  document.addEventListener("peguu:cart:refresh", () => refreshAll());
  document.addEventListener("peguu:cart:merged", (event) => {
    refreshAll(event.detail?.cart);
  });
  document.addEventListener("peguu:cart:ready", () => refreshAll());
  window.addEventListener("peguu:customer-authenticated", () => refreshAll());
  window.addEventListener("peguu:customer-logged-out", () => refreshAll());

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", () => initialize());
  } else {
    initialize();
  }

  document.addEventListener("shopify:section:load", (event) => {
    initialize(event.target);
  });
})();
